import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatEventNode, DisplayMessageChatEventNode} from 'llm/data-capture/DataCaptureTypes';
import DataCaptureAssistantMessageBubble from 'llm/data-capture/messages/DataCaptureAssistantMessageBubble';
import {DataCaptureFailureIndicator} from 'llm/data-capture/messages/DataCaptureFailureIndicator';
import DataCaptureProgress from 'llm/data-capture/messages/DataCaptureProgress';
import DataCaptureUserMessageBubble from 'llm/data-capture/messages/DataCaptureUserMessageBubble';

function filterDisplayEvents(events: Array<ChatEventNode>): Array<DisplayMessageChatEventNode> {
  return events.filter(
    (event): event is DisplayMessageChatEventNode => event.__typename === 'DisplayMessageChatEventNode'
  );
}

export interface DataCaptureMessageThreadProps {
  events: Array<ChatEventNode>;
  hadFailure: boolean;
}

/**
 * Display for the current chat conversation.
 */
export function DataCaptureMessageThread({events, hadFailure}: DataCaptureMessageThreadProps): JSX.Element {
  const displayEvents = filterDisplayEvents(events);
  // If the most recent message is from the user, show a progress indicator because
  // we're waiting on LLM response. We may want to change this in the future to better
  // handle more complex interactions and error states.
  const shouldShowProgress =
    displayEvents.length > 0 && displayEvents[displayEvents.length - 1].role === LLMRole.USER && !hadFailure;

  return (
    <div className={css(styles.threadContainer)}>
      {displayEvents.map((event) => {
        if (event.role === LLMRole.USER) {
          return <DataCaptureUserMessageBubble key={event.id} event={event} />;
        } else {
          return <DataCaptureAssistantMessageBubble key={event.id} event={event} />;
        }
      })}
      {shouldShowProgress && <DataCaptureProgress />}
      {hadFailure && <DataCaptureFailureIndicator />}
    </div>
  );
}

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: '12px',
    height: '100%',
    width: '100%',
  },
});
